import React, { useState, useEffect } from "react";
import {
  Activity,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Trash2,
  RotateCcw,
  ChevronLeft,
  Eye,
  EyeOff,
} from "lucide-react";
import { supabase } from "../../../lib/supabase";
import {
  queueMonitoringService,
  Queue,
  QueueJob,
  QueueStatsResponse,
} from "../../../services/QueueMonitoringService";

type JobStatus = "waiting" | "active" | "failed" | "completed" | "delayed";

interface QueueWithCompany extends Queue {
  companyName?: string;
}

const RESERVATION_WAREHOUSE_QUEUE = "reservation-warehouse";
const AVAILABILITY_WEBHOOKS_QUEUE = "availability-webhooks";

const DEFAULT_PLATFORM_QUEUES: Queue[] = [
  {
    name: RESERVATION_WAREHOUSE_QUEUE,
    waiting: 0,
    active: 0,
    completed: 0,
    failed: 0,
    delayed: 0,
    total: 0,
  },
  {
    name: AVAILABILITY_WEBHOOKS_QUEUE,
    waiting: 0,
    active: 0,
    completed: 0,
    failed: 0,
    delayed: 0,
    total: 0,
  },
];

function mergeWithPlatformQueues(queues: Queue[]): Queue[] {
  const queueMap = new Map<string, Queue>();

  for (const queue of DEFAULT_PLATFORM_QUEUES) {
    queueMap.set(queue.name, queue);
  }

  for (const queue of queues) {
    queueMap.set(queue.name, queue);
  }

  return Array.from(queueMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
}

export function QueueMonitoring() {
  const [queues, setQueues] = useState<QueueWithCompany[]>([]);
  const [selectedQueue, setSelectedQueue] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<JobStatus>("waiting");
  const [jobs, setJobs] = useState<QueueJob[]>([]);
  const [queueStats, setQueueStats] = useState<QueueStatsResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [pagination, setPagination] = useState({
    start: 0,
    limit: 50,
    total: 0,
  });
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Extract SaaS ID from queue name
  // Queue names can be in various formats:
  // - "events:saas-{uuid}" (with colon and saas prefix)
  // - "events-{uuid}" (with hyphen, direct UUID)
  // - "saas-{uuid}" (with saas prefix)
  // - Direct UUID
  const extractSaaSId = (queueName: string): string | null => {
    // Handle "events:" prefix (with colon)
    let nameWithoutPrefix = queueName.replace(/^events:/, "");
    
    // Handle "events-" prefix (with hyphen) - extract UUID directly
    if (nameWithoutPrefix.startsWith("events-")) {
      const uuid = nameWithoutPrefix.replace(/^events-/, "");
      // Validate it looks like a UUID
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid)) {
        return uuid;
      }
    }
    
    // Check if it starts with "saas-"
    if (nameWithoutPrefix.startsWith("saas-")) {
      // Extract UUID after "saas-"
      const uuid = nameWithoutPrefix.replace(/^saas-/, "");
      // Validate it looks like a UUID (basic check)
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid)) {
        return uuid;
      }
    }
    
    // If queue name is just a UUID, return it
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(nameWithoutPrefix)) {
      return nameWithoutPrefix;
    }
    
    return null;
  };

  const getQueueLabel = (queueName: string): string | undefined => {
    if (queueName === RESERVATION_WAREHOUSE_QUEUE) {
      return "Reservation Warehouse";
    }
    if (queueName === AVAILABILITY_WEBHOOKS_QUEUE) {
      return "Availability Webhooks";
    }
    return undefined;
  };

  const getQueueCompanyName = (queue: QueueWithCompany): string => {
    const queueLabel = getQueueLabel(queue.name);
    if (queueLabel) {
      return queueLabel;
    }
    return queue.companyName || "Unknown";
  };

  // Load company names for queues
  const loadCompanyNames = async (queues: Queue[]): Promise<QueueWithCompany[]> => {
    // Extract unique SaaS IDs from queue names
    const saasIds = new Set<string>();
    queues.forEach((queue) => {
      const saasId = extractSaaSId(queue.name);
      if (saasId) {
        saasIds.add(saasId);
      }
    });

    if (saasIds.size === 0) {
      return queues.map((q) => ({ ...q, companyName: undefined }));
    }

    // Fetch company names from saas_details
    try {
      const { data: saasDetails, error: saasError } = await supabase
        .from("saas_details")
        .select("id, company_name")
        .in("id", Array.from(saasIds));

      if (saasError) {
        console.error("Error fetching company names:", saasError);
        return queues.map((q) => ({ ...q, companyName: undefined }));
      }

      // Create a map of saas_id -> company_name
      const companyMap = new Map<string, string>();
      (saasDetails || []).forEach((detail) => {
        companyMap.set(detail.id, detail.company_name);
      });

      // Map queues with company names
      return queues.map((queue) => {
        const saasId = extractSaaSId(queue.name);
        const companyName = saasId ? companyMap.get(saasId) : undefined;
        return { ...queue, companyName };
      });
    } catch (err) {
      console.error("Error loading company names:", err);
      return queues.map((q) => ({ ...q, companyName: undefined }));
    }
  };

  // Load queues
  const loadQueues = async () => {
    try {
      setError("");
      const response = await queueMonitoringService.getQueues();
      const queuesData = mergeWithPlatformQueues(response.queues || []);
      
      // Load company names for all queues
      const queuesWithCompanies = await loadCompanyNames(queuesData);
      setQueues(queuesWithCompanies);
    } catch (err) {
      console.error("Error loading queues:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load queues"
      );
      const queuesWithCompanies = await loadCompanyNames(
        mergeWithPlatformQueues([])
      );
      setQueues(queuesWithCompanies);
    } finally {
      setLoading(false);
    }
  };

  // Load jobs for selected queue
  const loadJobs = async (
    queueName: string,
    status: JobStatus,
    start: number = 0
  ) => {
    if (!queueName) return;

    try {
      setJobsLoading(true);
      setError("");
      const response = await queueMonitoringService.getJobs(
        queueName,
        status,
        start,
        pagination.limit
      );
      setJobs(response.jobs || []);
      setPagination({
        start: response.pagination.start,
        limit: response.pagination.limit,
        total: response.pagination.total,
      });
    } catch (err) {
      console.error("Error loading jobs:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load jobs"
      );
    } finally {
      setJobsLoading(false);
    }
  };

  // Load queue stats
  const loadQueueStats = async (queueName: string) => {
    if (!queueName) return;

    try {
      const response = await queueMonitoringService.getQueueStats(queueName);
      setQueueStats(response);
    } catch (err) {
      console.error("Error loading queue stats:", err);
      // Don't set error here, just log it
    }
  };

  // Initial load
  useEffect(() => {
    loadQueues();
  }, []);

  // Auto-refresh queues every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!selectedQueue) {
        loadQueues();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedQueue]);

  // Load jobs when queue or status changes
  useEffect(() => {
    if (selectedQueue) {
      loadJobs(selectedQueue, selectedStatus, 0);
      loadQueueStats(selectedQueue);
    }
  }, [selectedQueue, selectedStatus]);

  // Auto-refresh jobs every 5 seconds when viewing details
  useEffect(() => {
    if (!selectedQueue) return;

    const interval = setInterval(() => {
      loadJobs(selectedQueue, selectedStatus, pagination.start);
      loadQueueStats(selectedQueue);
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedQueue, selectedStatus, pagination.start]);

  // Handle retry job
  const handleRetryJob = async (queueName: string, jobId: string) => {
    if (!confirm("Are you sure you want to retry this job?")) return;

    try {
      setActionLoading(jobId);
      setError("");
      setSuccessMessage("");
      await queueMonitoringService.retryJob(queueName, jobId);
      setSuccessMessage("Job retried successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
      // Reload jobs
      await loadJobs(queueName, selectedStatus, pagination.start);
      await loadQueueStats(queueName);
    } catch (err) {
      console.error("Error retrying job:", err);
      setError(
        err instanceof Error ? err.message : "Failed to retry job"
      );
    } finally {
      setActionLoading(null);
    }
  };

  // Handle remove job
  const handleRemoveJob = async (queueName: string, jobId: string) => {
    if (!confirm("Are you sure you want to remove this job? This action cannot be undone.")) return;

    try {
      setActionLoading(jobId);
      setError("");
      setSuccessMessage("");
      await queueMonitoringService.removeJob(queueName, jobId);
      setSuccessMessage("Job removed successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
      // Reload jobs
      await loadJobs(queueName, selectedStatus, pagination.start);
      await loadQueueStats(queueName);
      await loadQueues(); // Refresh queue counts
    } catch (err) {
      console.error("Error removing job:", err);
      setError(
        err instanceof Error ? err.message : "Failed to remove job"
      );
    } finally {
      setActionLoading(null);
    }
  };

  // Toggle job data expansion
  const toggleJobExpansion = (jobId: string) => {
    const newExpanded = new Set(expandedJobs);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedJobs(newExpanded);
  };

  // Format timestamp
  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  // Extract entity type from job data
  const getEntityType = (job: QueueJob): string => {
    if (!job.data) return job.name || "N/A";
    
    // Check various possible locations for entity type
    const data = job.data;

    if (data.context?.eventType) {
      return data.context.eventType;
    }

    if (job.name === "lighthouse-fetch" || data.sourceEndpoint === "lighthouseReservations") {
      return "Lighthouse Fetch";
    }

    if (job.name === "webhook-fetch") {
      return "Webhook Fetch";
    }

    if (job.name === "persist") {
      return "Persist";
    }
    
    // Check for event_object field (most common in BullMQ jobs)
    if (data.event_object) {
      return data.event_object;
    }
    
    // Direct entity_type field
    if (data.entity_type) {
      return data.entity_type;
    }
    
    // Check for type field
    if (data.type) {
      return data.type;
    }
    
    // Check for eventType field
    if (data.eventType) {
      return data.eventType;
    }
    
    // Check for object_type field
    if (data.object_type) {
      return data.object_type;
    }
    
    // Check if data is an object with entity information
    if (typeof data === 'object' && data !== null) {
      // Check nested structures
      if (data.event) {
        if (data.event.event_object) return data.event.event_object;
        if (data.event.entity_type) return data.event.entity_type;
      }
      if (data.payload) {
        if (data.payload.event_object) return data.payload.event_object;
        if (data.payload.entity_type) return data.payload.entity_type;
      }
      if (data.webhook) {
        if (data.webhook.event_object) return data.webhook.event_object;
        if (data.webhook.entity_type) return data.webhook.entity_type;
      }
      // Check nested data object
      if (data.data && typeof data.data === 'object') {
        if (data.data.event_object) return data.data.event_object;
        if (data.data.entity_type) return data.data.entity_type;
      }
    }
    
    return "N/A";
  };

  // Format entity type for display (capitalize first letter)
  const formatEntityType = (entityType: string): string => {
    if (entityType === "N/A") return entityType;
    // Convert snake_case or kebab-case to title case
    return entityType
      .split(/[_-]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Extract hotel code from job data
  const getHotelCode = (job: QueueJob): string => {
    if (!job.data) return "N/A";
    
    const data = job.data;

    if (data.context?.hotelCode) {
      return data.context.hotelCode || "N/A";
    }
    
    // Check for hotelCode field (most common)
    if (data.hotelCode) {
      return data.hotelCode || "N/A";
    }
    
    // Check for hotel_code field
    if (data.hotel_code) {
      return data.hotel_code || "N/A";
    }
    
    // Check nested structures
    if (typeof data === 'object' && data !== null) {
      // Check nested data object
      if (data.data && typeof data.data === 'object') {
        if (data.data.hotelCode) return data.data.hotelCode || "N/A";
        if (data.data.hotel_code) return data.data.hotel_code || "N/A";
      }
      // Check event object
      if (data.event) {
        if (data.event.hotelCode) return data.event.hotelCode || "N/A";
        if (data.event.hotel_code) return data.event.hotel_code || "N/A";
      }
      // Check payload object
      if (data.payload) {
        if (data.payload.hotelCode) return data.payload.hotelCode || "N/A";
        if (data.payload.hotel_code) return data.payload.hotel_code || "N/A";
      }
    }
    
    return "N/A";
  };

  // Get status badge color
  const getStatusBadgeClass = (status: JobStatus) => {
    switch (status) {
      case "waiting":
        return "bg-yellow-100 text-yellow-800";
      case "active":
        return "bg-blue-100 text-blue-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "delayed":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get health status color
  const getHealthStatusClass = (status: string) => {
    switch (status) {
      case "healthy":
        return "text-green-600";
      case "warning":
        return "text-yellow-600";
      case "critical":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="text-gray-500">Loading queues...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <Activity className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-900">Queue Monitoring</h2>
          </div>
          <div className="flex items-center space-x-2">
            {!selectedQueue && (
              <button
                onClick={loadQueues}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            )}
            {selectedQueue && (
              <button
                onClick={() => {
                  setSelectedQueue(null);
                  setSelectedStatus("waiting");
                  setJobs([]);
                  setQueueStats(null);
                }}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Queues
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-green-800">{successMessage}</span>
            </div>
          </div>
        )}

        {!selectedQueue ? (
          // Queue List View
          <div>
            {queues.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No queues found.</p>
              </div>
            ) : (
              <div className="w-full [&_.table-header]:px-3 [&_.table-cell]:px-3 [&_.table-cell]:whitespace-normal [&_.table-cell]:break-words">
                <table className="w-full table-auto divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="table-header">Queue Name</th>
                      <th className="table-header">Company</th>
                      <th className="table-header">Waiting</th>
                      <th className="table-header">Active</th>
                      <th className="table-header">Completed</th>
                      <th className="table-header">Failed</th>
                      <th className="table-header">Delayed</th>
                      <th className="table-header">Total</th>
                      <th className="table-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {queues.map((queue) => (
                      <tr key={queue.name} className="table-row">
                        <td className="table-cell font-medium">{queue.name}</td>
                        <td className="table-cell">
                          {getQueueCompanyName(queue) !== "Unknown" ? (
                            <span className="font-medium text-gray-900">
                              {getQueueCompanyName(queue)}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">Unknown</span>
                          )}
                        </td>
                        <td className="table-cell">{queue.waiting}</td>
                        <td className="table-cell">{queue.active}</td>
                        <td className="table-cell">{queue.completed}</td>
                        <td className="table-cell">
                          {queue.failed > 0 ? (
                            <span className="status-badge status-error">
                              {queue.failed}
                            </span>
                          ) : (
                            <span>{queue.failed}</span>
                          )}
                        </td>
                        <td className="table-cell">{queue.delayed}</td>
                        <td className="table-cell font-medium">
                          {queue.total}
                        </td>
                        <td className="table-cell">
                          <button
                            onClick={() => setSelectedQueue(queue.name)}
                            title="View Details"
                            aria-label="View Details"
                            className="flex items-center px-2 py-1 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          // Queue Details View
          <div>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Queue: {getQueueLabel(selectedQueue) || selectedQueue}
              </h3>
              {getQueueLabel(selectedQueue) && (
                <p className="text-sm text-gray-500 mb-2">{selectedQueue}</p>
              )}
              {selectedQueue === RESERVATION_WAREHOUSE_QUEUE && (
                <p className="text-sm text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-md px-3 py-2 mb-4">
                  Lighthouse reservation sync jobs appear here as{" "}
                  <span className="font-medium">lighthouse-fetch</span> (not in the
                  Lighthouse SaaS events queue). After calling the Lighthouse
                  reservations API, check Completed jobs for{" "}
                  <span className="font-medium">Lighthouse Fetch</span>.
                </p>
              )}

              {/* Queue Statistics */}
              {queueStats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white p-4 border border-gray-200 rounded-md">
                    <div className="text-sm text-gray-500">Health Status</div>
                    <div
                      className={`text-lg font-semibold ${getHealthStatusClass(
                        queueStats.health.status
                      )}`}
                    >
                      {queueStats.health.status.toUpperCase()}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {queueStats.health.isPaused ? "Paused" : "Active"}
                    </div>
                  </div>
                  <div className="bg-white p-4 border border-gray-200 rounded-md">
                    <div className="text-sm text-gray-500">Total Jobs</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {queueStats.stats.total}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {queueStats.stats.active} active
                    </div>
                  </div>
                  <div className="bg-white p-4 border border-gray-200 rounded-md">
                    <div className="text-sm text-gray-500">Failed Jobs</div>
                    <div className="text-lg font-semibold text-red-600">
                      {queueStats.stats.failed}
                    </div>
                    {queueStats.recentFailures.length > 0 && (
                      <div className="text-xs text-gray-400 mt-1">
                        {queueStats.recentFailures.length} recent
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status Filter Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                  {(
                    [
                      "waiting",
                      "active",
                      "failed",
                      "completed",
                      "delayed",
                    ] as JobStatus[]
                  ).map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setSelectedStatus(status);
                        setPagination({ ...pagination, start: 0 });
                      }}
                      className={`
                        flex items-center py-4 px-1 border-b-2 font-medium text-sm
                        ${
                          selectedStatus === status
                            ? "border-indigo-500 text-indigo-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }
                      `}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Jobs Table */}
              {jobsLoading ? (
                <div className="text-center py-8">
                  <div className="text-gray-500">Loading jobs...</div>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    No {selectedStatus} jobs found in this queue.
                  </p>
                </div>
              ) : (
                <div className="w-full [&_.table-header]:px-3 [&_.table-cell]:px-3 [&_.table-cell]:whitespace-normal [&_.table-cell]:break-words">
                  <table className="w-full table-auto divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="table-header">Job ID</th>
                        <th className="table-header">Job Name</th>
                        <th className="table-header">Hotel Code</th>
                        <th className="table-header">Entity</th>
                        <th className="table-header">Data</th>
                        <th className="table-header">Attempts</th>
                        {selectedStatus === "failed" && (
                          <th className="table-header">Failed Reason</th>
                        )}
                        <th className="table-header">Timestamp</th>
                        <th className="table-header">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {jobs.map((job) => (
                        <React.Fragment key={job.id}>
                          <tr className="table-row">
                            <td className="table-cell font-mono text-xs">
                              {job.id.substring(0, 8)}...
                            </td>
                            <td className="table-cell">{job.name}</td>
                            <td className="table-cell">
                              {getHotelCode(job) !== "N/A" ? (
                                <span className="font-medium text-gray-900">
                                  {getHotelCode(job)}
                                </span>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </td>
                            <td className="table-cell">
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                {formatEntityType(getEntityType(job))}
                              </span>
                            </td>
                            <td className="table-cell">
                              <button
                                onClick={() => toggleJobExpansion(job.id)}
                                className="flex items-center text-indigo-600 hover:text-indigo-800"
                              >
                                {expandedJobs.has(job.id) ? (
                                  <>
                                    <EyeOff className="h-4 w-4 mr-1" />
                                    Hide
                                  </>
                                ) : (
                                  <>
                                    <Eye className="h-4 w-4 mr-1" />
                                    View
                                  </>
                                )}
                              </button>
                            </td>
                            <td className="table-cell">
                              {job.attemptsMade} / {job.opts.attempts || 1}
                            </td>
                            {selectedStatus === "failed" && (
                              <td className="table-cell text-sm text-red-600 max-w-md truncate">
                                {job.failedReason || "Unknown error"}
                              </td>
                            )}
                            <td className="table-cell text-sm">
                              {formatTimestamp(job.timestamp)}
                            </td>
                            <td className="table-cell">
                              <div className="flex items-center space-x-2">
                                {selectedStatus === "failed" && (
                                  <button
                                    onClick={() =>
                                      handleRetryJob(selectedQueue, job.id)
                                    }
                                    disabled={actionLoading === job.id}
                                    className="flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 disabled:opacity-50"
                                  >
                                    <RotateCcw
                                      className={`h-3 w-3 mr-1 ${
                                        actionLoading === job.id
                                          ? "animate-spin"
                                          : ""
                                      }`}
                                    />
                                    Retry
                                  </button>
                                )}
                                <button
                                  onClick={() =>
                                    handleRemoveJob(selectedQueue, job.id)
                                  }
                                  disabled={actionLoading === job.id}
                                  className="flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 disabled:opacity-50"
                                >
                                  <Trash2
                                    className={`h-3 w-3 mr-1 ${
                                      actionLoading === job.id
                                        ? "animate-spin"
                                        : ""
                                    }`}
                                  />
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedJobs.has(job.id) && (
                            <tr>
                              <td colSpan={7} className="table-cell bg-gray-50">
                                <div className="p-4">
                                  <div className="text-sm font-medium text-gray-700 mb-2">
                                    Job Data:
                                  </div>
                                  <pre className="bg-white p-3 rounded border border-gray-200 text-xs overflow-x-auto">
                                    {JSON.stringify(job.data, null, 2)}
                                  </pre>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {pagination.total > pagination.limit && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Showing {pagination.start + 1} to{" "}
                        {Math.min(
                          pagination.start + pagination.limit,
                          pagination.total
                        )}{" "}
                        of {pagination.total} jobs
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            const newStart = Math.max(
                              0,
                              pagination.start - pagination.limit
                            );
                            setPagination({ ...pagination, start: newStart });
                            loadJobs(selectedQueue, selectedStatus, newStart);
                          }}
                          disabled={pagination.start === 0}
                          className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => {
                            const newStart = pagination.start + pagination.limit;
                            setPagination({ ...pagination, start: newStart });
                            loadJobs(selectedQueue, selectedStatus, newStart);
                          }}
                          disabled={
                            pagination.start + pagination.limit >=
                            pagination.total
                          }
                          className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
